import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import ChartBlock from '../ChartBlock';
import ImageGrid from '../ImageGrid';
import MermaidBlock from './MermaidBlock';
import { CopyIcon, CheckIcon } from '../icons';

function CopyableCodeBlock({ children, ...props }) {
  const [copied, setCopied] = useState(false);
  const preRef = React.useRef(null);

  const handleCopy = useCallback(() => {
    const text = preRef.current?.textContent ?? '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <div className="md-pre-wrapper">
      <button
        className={`md-pre-copy-btn${copied ? ' md-pre-copy-btn--copied' : ''}`}
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy code'}
        aria-label={copied ? 'Copied!' : 'Copy code'}
      >
        {copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
      </button>
      <pre ref={preRef} className="md-pre" {...props}>{children}</pre>
    </div>
  );
}

// Accepts isStreaming so mermaid blocks know not to render mid-stream
function buildMarkdownComponents(isStreaming) {
  return {
    table: props => <table className="md-table" {...props} />,
    th:    props => <th className="md-th" {...props} />,
    td:    props => <td className="md-td" {...props} />,
    code: ({ inline, className, children, node, ...props }) => {
      const language = (className ?? '').replace('language-', '');
      if (!inline && language === 'mermaid') {
        // remark splits ```mermaid <diagram-type>\n<body>``` into
        // lang="mermaid", meta="<diagram-type>", children="<body>"
        // We must reunite them so mermaid.js sees the diagram type declaration.
        const meta = node?.data?.meta ?? node?.properties?.meta ?? '';
        const rawCode = [meta, String(children ?? '')].filter(Boolean).join('\n');        
        return (
          <MermaidBlock
            code={rawCode.trim()}
            isStreaming={isStreaming}
          />
        );
      }
      return inline
        ? <code className="md-code-inline" {...props}>{children}</code>
        : <code className="md-code-block" {...props}>{children}</code>;
    },
    pre: CopyableCodeBlock,
  };
}

function normaliseLatexDelimiters(content) {
  return content
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => `$$${inner}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => `$${inner}$`);
}

/**
 * During streaming the LLM emits the image fence incrementally:
 *   ```images\n{...partial json...
 * The closing ``` hasn't arrived yet, so parseSegments can't match it and
 * the raw JSON leaks into the text renderer.
 *
 * maskIncompleteFences detects an unclosed ```images fence at the end of the
 * content string and replaces it with a stable placeholder token so the user
 * sees a loading indicator instead of raw JSON.
 *
 * A complete fence (has a closing ```) is never touched by this regex.
 */
function maskIncompleteFences(content) {
  return content.replace(
    /```images[ \t]*\r?\n(?![\s\S]*````)[\s\S]*$/,
    '\n```images-loading```'
  );
}

/**
 * For images/chart segments, clip the captured content to the last closing
 * brace or bracket. This discards any trailing text the LLM may have written
 * after the JSON payload but before the closing ``` of the fence.
 *
 * Example of what this fixes:
 *   ```images
 *   {"type":"images","query":"cats",...}
 *   Here are some cat images for you!
 *   ```
 *
 * Without this, the segment content includes the sentence, corrupting
 * JSON.parse in ImageGrid / ChartBlock.
 */
function trimToJsonContent(s) {
  const last = Math.max(s.lastIndexOf('}'), s.lastIndexOf(']'));
  return last !== -1 ? s.slice(0, last + 1) : s;
}

function parseSegments(content) {
  if (!content) return [];
  const segments = [];
  const regex = /```(chart|images|images-loading)[ \t]*\r?\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex)
      segments.push({ type: 'text', content: content.slice(lastIndex, match.index) });

    const type = match[1];
    const raw  = match[2].trim();

    // For JSON-bearing segment types, clip to the last } / ] to drop any
    // trailing commentary the LLM wrote inside the fence after the payload.
    const segContent = (type === 'images' || type === 'chart')
      ? trimToJsonContent(raw)
      : raw;

    segments.push({ type, content: segContent });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length)
    segments.push({ type: 'text', content: content.slice(lastIndex) });
  return segments;
}

export default function MessageContent({ content, isStreaming = false }) {
  const processed = isStreaming ? maskIncompleteFences(content ?? '') : (content ?? '');
  const segments = parseSegments(processed);
  const markdownComponents = useMemo(() => buildMarkdownComponents(isStreaming), [isStreaming]);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'chart') {
          return <ChartBlock key={i} raw={seg.content} />;
        }
        if (seg.type === 'images') {
          return <ImageGrid key={i} raw={seg.content} />;
        }
        if (seg.type === 'images-loading') {
          return (
            <div key={i} className="image-grid-block">
              <div className="image-grid-header">
                <span className="image-grid-title">Loading images…</span>
              </div>
              <div className="image-grid-loading">
                <span className="image-grid-loading-dots">
                  <span /><span /><span />
                </span>
              </div>
            </div>
          );
        }
        const normalised = normaliseLatexDelimiters(seg.content);
        return normalised.trim() ? (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={markdownComponents}
          >
            {normalised}
          </ReactMarkdown>
        ) : null;
      })}
    </>
  );
}